import clsx from 'clsx';
import {
  useEffect,
  useMemo,
  useRef,
  type MouseEventHandler as ReactMouseEventHandler,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { useIntl } from 'react-intl';

import { ProgressCircle } from '@dynatrace/strato-components/content';
import {
  type DataTestId,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';

import { expandButtonCSS } from './Control.sty.js';
import { useChipGroupContext } from './hooks/useChipGroupContext.js';
import { useInternalChipGroupContext } from './hooks/useInternalChipGroupContext.js';
import { ChipGroupActions } from './state/chip-group-state-reducer.js';
import { useContainerSize } from '../../core/hooks/useContainerSize.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';

//#region Types

/**
 * @public
 */
export interface ChipGroupControlProps
  extends WithChildren,
    StylingProps,
    DataTestId {
  /**
   * Whether to show more chips or collapse chips.
   * If undefined, the action is inferred.
   * If no more items are available and all items fit in one line,
   * the control is automatically hidden unless display is set.
   * @defaultValue inferred from the `expanded` / `defaultExpanded` prop of the `ChipGroup`
   */
  display?: 'more' | 'less';

  /**
   * Whether to render `Show more` or `Show x more` by default.
   * If true, the count is calculated automatically. The count can be overridden
   * by passing a number.
   * @defaultValue undefined
   */
  count?: boolean | number;

  /**
   * Whether the control should show the loading indicator.
   * @defaultValue false
   */
  loading?: boolean;

  /** Callback triggered when the control is interacted with. */
  onClick?: ReactMouseEventHandler<HTMLButtonElement>;
}
//#endregion

/**
 * @public
 */
export const Control = (props: ChipGroupControlProps) => {
  const {
    display,
    count,
    loading = false,
    onClick,
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
  }: ChipGroupControlProps = props;
  const intl = useIntl();

  const { focusProps, focusClassName, isFocusVisible } = useFocusRing({
    color: 'neutral',
  });

  const { hiddenKeys, visibleKeys } = useChipGroupContext();
  const internalChipGroupContext = useInternalChipGroupContext();
  const { chipGroupId, expanded, allChipsFit, setExpanded, dispatch } =
    internalChipGroupContext || {};

  const controlRef = useRef<HTMLButtonElement | null>(null);
  const { width: controlWidth } = useContainerSize(controlRef);
  const width = Math.ceil(controlWidth);

  const label = useMemo(() => {
    const moreOrLess = expanded && display !== 'more' ? 'less' : 'more';
    const hiddenChipsCount =
      count === true && hiddenKeys.length > 0 ? hiddenKeys.length : count;
    const showCount =
      typeof hiddenChipsCount === 'number' && moreOrLess === 'more';

    return intl.formatMessage(
      {
        defaultMessage: 'Show {count} {moreOrLess}',
        id: 'ARdI7d8cJxHnUayP',
        description: 'Label for the expand / collapse button.',
      },
      {
        moreOrLess: expanded && display !== 'more' ? 'less' : 'more',
        count: showCount ? hiddenChipsCount : undefined,
      },
    );
  }, [count, display, expanded, hiddenKeys, intl]);

  useEffect(() => {
    dispatch?.({
      type: ChipGroupActions.ADDCONTROL,
      payload: { width },
    });

    return () => {
      dispatch?.({
        type: ChipGroupActions.REMOVECONTROL,
        payload: {},
      });
    };
    // only for mount and unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch?.({
      type: ChipGroupActions.UPDATECONTROL,
      payload: {
        width: width === 0 ? undefined : width,
        show: display !== undefined ? 'always' : 'auto',
      },
    });
  }, [dispatch, display, width]);

  if (display === undefined && (allChipsFit || visibleKeys.length === 0)) {
    return null;
  }

  return (
    <button
      data-testid={dataTestId}
      ref={controlRef}
      onClick={(event: ReactMouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        setExpanded?.((prev) => (display === 'more' ? true : !prev));
      }}
      aria-controls={chipGroupId}
      aria-expanded={expanded}
      className={clsx(
        expandButtonCSS({ isFocusVisible }),
        focusClassName,
        consumerClassName,
      )}
      style={consumerStyle}
      {...focusProps}
    >
      {loading && (
        <ProgressCircle
          size="small"
          aria-label={intl.formatMessage({
            defaultMessage: 'Loading',
            id: 'cycz+bYG+VH7Of4e',
            description: 'Label for the loading state.',
          })}
        />
      )}
      {children ?? label}
    </button>
  );
};
